import React from "react";
import { BnbLabel, useBnbOptionalLabel } from "../../../utils/labels";
import { useBnbSelector } from "../../../redux/store";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import MonthlyRateDisclaimer from "./price/MonthlyRateDisclaimer";
import Price from "./price/Price";
import * as Styles from "../../../../../../common-deprecated/styles/v2/toyota/custom/PaymentDetails";
import { formatPrice } from "../../../../../../common-deprecated/Globalize";
import { getBnbMonthlyRateFormat, getBnbPriceConfig } from "../../../utils/settings/bnbFinanceSettingUtils";
import { showExternalPriceTax } from "../../../utils/settings/bnbSettingUtils";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { showPrice } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";

const PaymentDetails = (): JSX.Element => {
    const car = useBnbSelector((state) => state.car);
    const totalPrice = useBnbSelector((state) => state.basket.total);
    const financeInfo = useBnbSelector((state) => state.finance.info);
    const financeOption = useBnbSelector(({ utils }) => utils.financeOption);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const step = useBnbSelector(({ navigation }) => navigation.step);
    const total = useBnbSelector(({ basket }) => basket.total);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const nonFinanceableServiceProducts = useBnbSelector((state) =>
        state.basket.serviceProducts.filter((serviceProduct) => !serviceProduct.financeable),
    );

    const cultureName = commonSettings.culture.name;
    const tax = formatPrice(parseInt(total.discountTax as any), cultureName).replace(" ", "&nbsp;");
    const priceWithoutTax = formatPrice(parseInt(total.discountWithoutTax as any), cultureName).replace(" ", "&nbsp;");
    const monthlyFormat = getBnbMonthlyRateFormat(commonSettings, car.model ? car.model.internalCode : "");
    const showTotalPrice =
        showPrice(commonSettings) && getBnbPriceConfig(commonSettings, car.model.internalCode, step).priceEnabled;

    const nonFinanceAblePrice = formatPrice(
        nonFinanceableServiceProducts.reduce(
            (totalPriceAcc, serviceProduct) => totalPriceAcc + serviceProduct.price.cash,
            0,
        ),
        cultureName,
    );

    const [wltpTotalPriceBeforeTax, wltpTax] = useBnbOptionalLabel(["wltpTotalPriceBeforeTax", "wltpTax"]);

    return (
        <>
            {showTotalPrice && (
                <Styles.Wrapper data-testid="paymentDetails">
                    {/* WLTP tax */}
                    {total.isExternalPrice && !showExternalPriceTax(bnbSettings) && (
                        <>
                            {wltpTotalPriceBeforeTax && (
                                <>
                                    <Styles.ListItemTerm>{wltpTotalPriceBeforeTax}</Styles.ListItemTerm>
                                    <Styles.ListItemValue>
                                        <span dangerouslySetInnerHTML={{ __html: priceWithoutTax }} />
                                    </Styles.ListItemValue>
                                </>
                            )}
                            {wltpTax && (
                                <>
                                    <Styles.ListItemTerm>
                                        <BnbLabel label="wltpTax" />
                                    </Styles.ListItemTerm>
                                    <Styles.ListItemValue>
                                        <span dangerouslySetInnerHTML={{ __html: tax }} />
                                    </Styles.ListItemValue>
                                </>
                            )}
                        </>
                    )}

                    {/* Total price */}
                    <Styles.ListItemTerm isBold hasTotalPrice={isRTL(commonSettings)} data-rtl={isRTL(commonSettings)}>
                        <BnbLabel
                            label={financeOption === "cash" ? "basketTotal" : "basketTotalMonthlyCosts"}
                            fallbackLabel="basketTotal"
                        />
                    </Styles.ListItemTerm>
                    <Styles.ListItemValue hasTotalPrice={isRTL(commonSettings)} data-rtl={isRTL(commonSettings)}>
                        <Price price={totalPrice} financeItemId="total" forceHideMonthlyDisclaimer showLoader />
                    </Styles.ListItemValue>

                    {/* Service plans pay upfront price */}
                    {financeOption === "monthly" && nonFinanceableServiceProducts.length > 0 && (
                        <>
                            <dt>
                                <BnbLabel label="nonFinanceableBasketTotal" />
                            </dt>
                            <dd>
                                <span dangerouslySetInnerHTML={{ __html: nonFinanceAblePrice }} />
                            </dd>
                        </>
                    )}

                    {/* finance term */}
                    {step === NavStep.Buy && financeOption === "monthly" && !!financeInfo.term && (
                        <>
                            <Styles.ListItemTerm>
                                <BnbLabel label="monthlyTermTitle" />
                            </Styles.ListItemTerm>
                            <Styles.ListItemValue>
                                <Styles.RegularText>
                                    <BnbLabel label="monthlyTerm" params={{ term: financeInfo.term }} />
                                </Styles.RegularText>
                            </Styles.ListItemValue>
                        </>
                    )}
                </Styles.Wrapper>
            )}

            {financeOption === "monthly" && (
                <Styles.MonthlyRateDisclaimer>
                    <MonthlyRateDisclaimer price={totalPrice} priceFormat={monthlyFormat} financeInfo={financeInfo} />
                </Styles.MonthlyRateDisclaimer>
            )}
        </>
    );
};

export default PaymentDetails;
